import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  ChevronRight, MousePointer, Search, Filter, Shield, 
  ExternalLink, Clock, Users, TrendingUp, AlertTriangle,
  Download, Eye, MoreVertical
} from "lucide-react";

export default function Applications() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [activeCardFilter, setActiveCardFilter] = useState<string | null>(null); // 'total' | 'active' | 'thirdParty' | 'highRisk' | null

  const { data: applications = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/applications"],
    refetchInterval: 60000, // Refresh every minute
  });

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case "low": return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-purple-400";
      case "medium": return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400";
      case "high": return "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400";
      case "critical": return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400";
      default: return "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400";
    }
  };

  const getAppIcon = (name: string, type: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes("google")) return "🔴";
    if (nameLower.includes("hubspot")) return "🧡";
    if (nameLower.includes("facebook") || nameLower.includes("meta")) return "🔵";
    if (nameLower.includes("jquery")) return "💛";
    if (nameLower.includes("cloudflare")) return "🧡";
    if (type === "payment") return "💳";
    if (type === "analytics") return "📊";
    if (type === "social") return "🌐";
    if (type === "security-risk") return "⚠️";
    if (type === "monitoring") return "📈";
    return "⚪";
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'analytics': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'marketing': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'payment': return 'bg-green-100 text-green-800 border-green-200';
      case 'social': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'library': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'monitoring': return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'security-risk': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const handleCardClick = (filterType: 'total' | 'active' | 'thirdParty' | 'highRisk') => {
    if (activeCardFilter === filterType) {
      setActiveCardFilter(null);
    } else {
      setActiveCardFilter(filterType);
    }
  };

  const filteredApplications = (applications as any[])?.filter((app: any) => {
    const matchesSearch = 
      app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === "all" || app.type === filterType;
    
    // Apply card filter
    let matchesCardFilter = true;
    if (activeCardFilter === 'active') {
      matchesCardFilter = app.isActive === true;
    } else if (activeCardFilter === 'thirdParty') {
      matchesCardFilter = app.isThirdParty === true;
    } else if (activeCardFilter === 'highRisk') {
      matchesCardFilter = app.riskLevel === 'high' || app.riskLevel === 'critical';
    }
    // 'total' or null means show all
    
    return matchesSearch && matchesFilter && matchesCardFilter;
  }) || [];

  const summary = {
    total: (applications as any[])?.length || 0,
    thirdParty: (applications as any[])?.filter((app: any) => app.isThirdParty).length || 0,
    highRisk: (applications as any[])?.filter((app: any) => app.riskLevel === 'high').length || 0,
    active: (applications as any[])?.filter((app: any) => app.isActive).length || 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Web Application Security
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Monitor and analyze web applications and third-party services for security vulnerabilities
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/applications';
              link.download = `applications-report-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Badge variant="secondary" className="text-xs">
            <Shield className="h-3 w-3 mr-1" />
            {summary.highRisk} High Risk
          </Badge>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'total' ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950' : ''
          }`}
          onClick={() => handleCardClick('total')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'total' ? 'bg-blue-200' : 'bg-blue-100'
              }`}>
                <MousePointer className={`h-5 w-5 ${
                  activeCardFilter === 'total' ? 'text-blue-700' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.total}
                </div>
                <div className="text-xs text-slate-600">Detected Apps</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'active' ? 'ring-2 ring-green-500 bg-green-50 dark:bg-green-950' : ''
          }`}
          onClick={() => handleCardClick('active')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'active' ? 'bg-green-200' : 'bg-green-100'
              }`}>
                <Users className={`h-5 w-5 ${
                  activeCardFilter === 'active' ? 'text-green-700' : 'text-purple-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'active' ? 'text-green-700' : 'text-purple-600'
                }`}>
                  {summary.active}
                </div>
                <div className="text-xs text-slate-600">Currently Active</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'thirdParty' ? 'ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-950' : ''
          }`}
          onClick={() => handleCardClick('thirdParty')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'thirdParty' ? 'bg-orange-200' : 'bg-orange-100'
              }`}>
                <ExternalLink className={`h-5 w-5 ${
                  activeCardFilter === 'thirdParty' ? 'text-orange-700' : 'text-orange-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'thirdParty' ? 'text-orange-700' : 'text-orange-600'
                }`}>
                  {summary.thirdParty}
                </div>
                <div className="text-xs text-slate-600">External Services</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'highRisk' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-950' : ''
          }`}
          onClick={() => handleCardClick('highRisk')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'highRisk' ? 'bg-red-200' : 'bg-red-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'highRisk' ? 'text-red-700' : 'text-red-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'highRisk' ? 'text-red-700' : 'text-red-600'
                }`}>
                  {summary.highRisk}
                </div>
                <div className="text-xs text-slate-600">Security Alerts</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="analytics">Analytics</option>
            <option value="marketing">Marketing</option>
            <option value="payment">Payment</option>
            <option value="social">Social</option>
            <option value="library">Library</option>
            <option value="monitoring">Monitoring</option>
          </select>
        </div>
      </div>

      {/* Applications List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MousePointer className="h-5 w-5 text-blue-600" />
            Security Applications ({filteredApplications.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading applications...</span>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MousePointer className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No Applications Detected</div>
              <div className="text-sm">
                {searchTerm || filterType !== "all" 
                  ? "Try adjusting your search or filter criteria."
                  : "No web applications have been detected in your recent security scans."}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredApplications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedApp(app)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-2xl">
                      {getAppIcon(app.name, app.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{app.name}</span>
                        <Badge className={`text-xs border ${getRiskLevelColor(app.riskLevel)}`}>
                          {app.riskLevel}
                        </Badge>
                        <Badge className={`text-xs border ${getTypeColor(app.type)}`}>
                          {app.type}
                        </Badge>
                        {app.isThirdParty && (
                          <Badge variant="outline" className="text-xs">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Third-Party
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                        {app.description}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>By {app.vendor}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last seen: {new Date(app.lastSeen).toLocaleDateString()}
                        </span>
                        <span>{app.scriptCount} script{app.scriptCount !== 1 ? 's' : ''}</span>
                        <span>{app.domains.length} domain{app.domains.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Application Details Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="text-2xl">{getAppIcon(selectedApp.name, selectedApp.type)}</div>
                Security Analysis
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedApp(null)}
                  className="ml-auto"
                >
                  ×
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Service Name</label>
                  <div className="font-medium">{selectedApp.name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Provider</label>
                  <div>{selectedApp.vendor}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Type</label>
                  <div>
                    <Badge className={`${getTypeColor(selectedApp.type)}`}>
                      {selectedApp.type}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Risk Level</label>
                  <div>
                    <Badge className={`${getRiskLevelColor(selectedApp.riskLevel)}`}>
                      {selectedApp.riskLevel.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Description</label>
                <div className="text-sm bg-slate-50 p-3 rounded border">
                  {selectedApp.description}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Script Count</label>
                  <div className="text-sm">{selectedApp.scriptCount}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">External Service</label>
                  <div className="flex items-center gap-2">
                    {selectedApp.isThirdParty ? (
                      <ExternalLink className="h-4 w-4 text-orange-500" />
                    ) : (
                      <Shield className="h-4 w-4 text-purple-500" />
                    )}
                    <span className="text-sm">
                      {selectedApp.isThirdParty ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                  <label className="text-sm font-medium text-slate-600">Associated Domains</label>
                <div className="space-y-1 mt-2">
                  {selectedApp.domains.map((domain, index) => (
                    <div key={index} className="text-sm bg-slate-100 p-2 rounded flex items-center gap-2">
                      <ExternalLink className="h-3 w-3 text-slate-500" />
                      {domain}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-600">Last Security Scan</label>
                <div className="text-sm">{new Date(selectedApp.lastSeen).toLocaleString()}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}